import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Medal, Crown, Star, ChevronUp, ChevronDown } from 'lucide-react';
import { referralApi } from '../../api';
import { useAppStore } from '../../store/useAppStore';

interface LeaderboardEntry {
  rank: number;
  telegramId: number;
  username: string;
  firstName: string;
  balance: number;
  level: number;
  referrals: number;
}

type LeaderboardType = 'balance' | 'referrals' | 'level';

export function Leaderboard() {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState<LeaderboardType>('balance');
  const [userRank, setUserRank] = useState<LeaderboardEntry | null>(null);

  const user = useAppStore((state) => state.user);

  useEffect(() => {
    fetchLeaderboard();
  }, [type]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const data = await referralApi.getLeaderboard(type);
      // Map API response to LeaderboardEntry format
      const mappedEntries = (data.entries || data.leaderboard || []).map((entry, idx) => ({
        rank: entry.rank || idx + 1,
        telegramId: 0,
        username: entry.username || 'Anonymous',
        firstName: entry.username || 'Anonymous',
        balance: entry.value || 0,
        level: 1,
        referrals: entry.value || 0,
      }));
      setEntries(mappedEntries);
      
      // Handle userRank - it's a number, not an object
      if (typeof data.userRank === 'number' && user) {
        setUserRank({
          rank: data.userRank,
          telegramId: parseInt(user.telegramId) || 0,
          username: user.username || 'You',
          firstName: user.firstName || 'You',
          balance: user.balance,
          level: user.level,
          referrals: 0,
        });
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-400" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-300" />;
      case 3:
        return <Medal className="w-6 h-6 text-orange-400" />;
      default:
        return <span className="text-white/60 font-bold">{rank}</span>;
    }
  };

  const getRankBg = (rank: number) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-500/30';
      case 2:
        return 'bg-gradient-to-r from-gray-400/20 to-gray-500/20 border-gray-400/30';
      case 3:
        return 'bg-gradient-to-r from-orange-500/20 to-red-500/20 border-orange-500/30';
      default:
        return 'bg-white/5';
    }
  };

  const getValue = (entry: LeaderboardEntry) => {
    switch (type) {
      case 'balance':
        return entry.balance.toLocaleString();
      case 'referrals':
        return entry.referrals.toString();
      case 'level':
        return `Lvl ${entry.level}`;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="card p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
            <Trophy className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="font-bold text-xl">Leaderboard</h2>
            <p className="text-white/60 text-sm">Top performers</p>
          </div>
        </div>

        {/* Type selector */}
        <div className="grid grid-cols-3 gap-2 bg-white/5 p-1 rounded-xl">
          {[
            { value: 'balance', label: 'Balance' },
            { value: 'referrals', label: 'Referrals' },
            { value: 'level', label: 'Level' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => setType(option.value as LeaderboardType)}
              className={`py-2 rounded-lg text-sm font-medium transition-all ${
                type === option.value
                  ? 'bg-primary text-white'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* User's rank */}
      {userRank && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-4 border border-primary/30"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
              <span className="font-bold">#{userRank.rank}</span>
            </div>
            <div className="flex-1">
              <p className="font-bold">Your Rank</p>
              <p className="text-white/50 text-sm">
                {getValue(userRank)} points
              </p>
            </div>
            {userRank.rank <= 10 ? (
              <div className="flex items-center gap-1 text-green-400">
                <ChevronUp className="w-4 h-4" />
                <span className="text-sm">Top 10!</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-white/50">
                <ChevronDown className="w-4 h-4" />
                <span className="text-sm">Keep going</span>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Leaderboard list */}
      <div className="card p-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-2">
            {entries.map((entry, index) => (
              <motion.div
                key={entry.telegramId}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`flex items-center gap-4 p-3 rounded-xl border transition-all ${
                  user?.telegramId === String(entry.telegramId)
                    ? 'border-primary/50 bg-primary/10'
                    : `border-transparent ${getRankBg(entry.rank)}`
                }`}
              >
                {/* Rank */}
                <div className="w-10 h-10 flex items-center justify-center">
                  {getRankIcon(entry.rank)}
                </div>

                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center">
                  <span className="font-bold">
                    {entry.firstName?.charAt(0).toUpperCase() || '?'}
                  </span>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {entry.firstName || `User ${entry.telegramId}`}
                    {entry.username && (
                      <span className="text-white/40 text-sm ml-1">
                        @{entry.username}
                      </span>
                    )}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-white/50">
                    <span>Level {entry.level}</span>
                    <span>•</span>
                    <span>{entry.referrals} refs</span>
                  </div>
                </div>

                {/* Value */}
                <div className="text-right">
                  <p className={`font-bold ${entry.rank <= 3 ? 'text-yellow-400' : ''}`}>
                    {getValue(entry)}
                  </p>
                </div>
              </motion.div>
            ))}

            {entries.length === 0 && (
              <p className="text-center text-white/50 py-8">No data available</p>
            )}
          </div>
        )}
      </div>

      {/* Rewards info */}
      <div className="card p-6">
        <h4 className="font-bold mb-4 flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-400" />
          Weekly Rewards
        </h4>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-yellow-500/20 to-orange-500/20">
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-400" />
              <span>1st Place</span>
            </div>
            <span className="font-bold text-yellow-400">100,000 pts</span>
          </div>
          <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-gray-400/20 to-gray-500/20">
            <div className="flex items-center gap-2">
              <Medal className="w-5 h-5 text-gray-300" />
              <span>2nd Place</span>
            </div>
            <span className="font-bold text-gray-300">50,000 pts</span>
          </div>
          <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-orange-500/20 to-red-500/20">
            <div className="flex items-center gap-2">
              <Medal className="w-5 h-5 text-orange-400" />
              <span>3rd Place</span>
            </div>
            <span className="font-bold text-orange-400">25,000 pts</span>
          </div>
        </div>
        <p className="text-white/40 text-xs text-center mt-4">
          Rewards distributed every Sunday at midnight UTC
        </p>
      </div>
    </motion.div>
  );
}
